<script setup>
import InputProvider from '../form/inputProvider.vue';
import {computed, onMounted, ref} from "vue";
import SlideoutWithSlots from "../../elements/slideoutWithSlots.vue";
import ButtonWithConfirmation from "../../buttons/buttonWithConfirmation.vue";
import ToggleSwitch from "../../forms/toggleSwitch.vue";

const emit = defineEmits(['close', 'update:chapter'])

const props = defineProps({
    courseType: {
        type: String,
        required: true
    },
    chapter: {
        type: Object,
        required: true
    },
})

const _chapter = ref(null)
const titleInput = ref(null)

const toDateInputValue = (value) => {
    if (!value) {
        return null
    }

    return String(value).split('T')[0]
}

const loadChapter = async () => {
    const res = await axios.get(route('api.course.backoffice.chapter.edit', {
        course_id: props.chapter.course_id,
        chapter_id: props.chapter.id
    }));

    _chapter.value = res.data.chapter
    _chapter.value.available_from = toDateInputValue(_chapter.value.available_from)
    _chapter.value.available_until = toDateInputValue(_chapter.value.available_until)
    setTimeout(function () {
        titleInput.value.focusInput()
    }, 1)
}

const save = async () => {
    const res = await axios.put(route('api.course.backoffice.chapter.update', {
        course_id: props.chapter.course_id,
        chapter_id: props.chapter.id
    }), _chapter.value);

    if (res.data.success) {
        emit('update:chapter', res.data.chapter)
    }
}

const cancel = () => {
    emit('close')
}

const chapterValidates = computed(() => {
    return _chapter.value?.title.length > 0
})

onMounted(() => {
    loadChapter();
})

</script>

<template>
    <slideout-with-slots
        @close="emit('close')"
        :title="chapter.title"
    >
        <template #actions>
            <div class="mb-2 flex justify-end space-x-2">
                <button-with-confirmation
                    @confirmed="cancel"
                    modalClass="absolute -top-2 -right-2 p-2 bg-white text-black rounded shadow-md z-10"
                    :buttonLabel="_mft('shared:cancel')"
                    buttonClass="text-wlPrimaryContrast"
                    :confirmButtonLabel="_mft('shared:confirm.continue')"
                    confirmButtonClass="myflow-basic-button--success whitespace-nowrap"
                    :confirm-text="_mft('shared:confirm.changesLost')"
                ></button-with-confirmation>

                <button-success
                    :disabled="! chapterValidates"
                    @click="save"
                >{{ _mft('shared:action.save') }}
                </button-success>
            </div>
        </template>
        <template #body>
            <div v-if="_chapter">
                <ui-tabbed
                    :track-state="false"
                    :responsive="false"
                >
                    <ui-tabbed-tab
                        :title="_mft('course:chapter.tab.settings')"
                        value="settings"
                        :active="true"
                    >
                        <input-text
                            v-model:content="_chapter.title"
                            :label="_mft('course:chapter.title')"
                            required="required"
                            name="title"
                            ref="titleInput"
                        />

                        <div class="flex justify-between items-center">
                            <input-label
                                :label="_mft('course:chapter.description')"
                            ></input-label>
                            <span
                                v-if="_chapter?.description"
                                class="text-xs text-gray-400"
                            >{{ _chapter.description.length }}/100</span>
                        </div>
                        <input-textarea
                            v-model:content="_chapter.description"
                            maxlength="100"
                        ></input-textarea>

                        <input-provider
                            :label="_mft('course:chapter.provider')"
                            name="provider_id"
                            v-model="_chapter.provider_id"
                        />

                        <toggle-switch
                            v-model:content="_chapter.published"
                            :label="_mft('course:chapter.published')"
                            name="published"
                        />

                        <input-date
                            v-model:content="_chapter.available_from"
                            :label="_mft('course:chapter.availableFrom')"
                            name="available_from"
                        />

                        <input-date
                            v-model:content="_chapter.available_until"
                            :label="_mft('course:chapter.availableUntil')"
                            name="available_until"
                        />

                    </ui-tabbed-tab>

                    <ui-tabbed-tab
                        v-if="courseType === 'class'"
                        :title="_mft('course:chapter.tab.thumbnail')"
                        value="thumbnail"
                    >
                        <input-label :label="_mft('course:chapter.thumbnail')"></input-label>
                        <p class="text-gray-600 italic">{{ _mft('course:chapter.thumbnail.instruction') }}</p>

                        <div
                            v-if="_chapter.thumbnail_media_bank_entry_id"
                            class="w-48 aspect-video flex flex-col items-center"
                        >
                            <media-bank-entries-display
                                :data-url="route('mediaBank.entries.show', _chapter.thumbnail_media_bank_entry_id)"
                            />

                            <button-danger
                                @click="_chapter.thumbnail_media_bank_entry_id = null"
                                class="mt-4"
                            >
                                {{ _mft('shared:action.remove') }}
                            </button-danger>
                        </div>

                        <input-media-bank-gallery
                            v-if="_chapter.thumbnail_media_bank_entry_id === null"
                            type="image"
                            v-model="_chapter.thumbnail_media_bank_entry_id"
                            @update:content="_chapter.thumbnail_media_bank_entry_id = $event"
                        />
                    </ui-tabbed-tab>
                </ui-tabbed>
            </div>
            <mf-spinner-medium
                v-else
                class="w-12 h-6"
            />
        </template>
    </slideout-with-slots>
</template>
